import * as R from '@spendesk/general-type-helpers/Result';
import { useState, useEffect } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { ScaWireTransferConfirmationModalContainer } from 'modules/StrongCustomerAuthentication';
import { type ScaState } from 'modules/StrongCustomerAuthentication/types';
import { useNotifications } from 'modules/app/notifications';
import { VerificationOfBeneficiariesModal } from 'modules/invoices/transfer/components/VerificationOfBeneficiariesModal';
import {
  useVerifyTransfersBeneficiaries,
  VerifyBeneficiariesQueryFactory,
} from 'modules/invoices/transfer/hooks';
import { type Beneficiary } from 'modules/invoices/transfer/models';
import { AlternateAuthenticationMethodModal } from 'src/auth/modules/StrongCustomerAuthentication/components/AlternateAuthenticationMethodModal/AlternateAuthenticationMethodModal';
import { useGetUserFactors } from 'src/auth/modules/StrongCustomerAuthentication/hooks/useGetUsersFactors';
import { type AuthenticationFactor } from 'src/auth/modules/StrongCustomerAuthentication/models/factors';
import { ScaWireTransferConfirmationEnrollmentModal } from 'src/core/modules/StrongCustomerAuthentication/components/ScaWireTransferConfirmationEnrollmentModal';

import { type Props as WireTransferApproverModalContainerProps } from './WireTransferApproverModalContainer';
import { WireTransfersApprovedModal } from './WireTransfersApprovedModal';
import { WireTransfersDeclinedModal } from './WireTransfersDeclinedModal';
import { WireTransfersNotEnoughFundsModal } from './WireTransfersNotEnoughFundsModal';
import { WireTransfersSummaryModal } from './WireTransfersSummaryModal';

export enum ApproverModalStep {
  NotEnoughFunds,
  Summary,
  MfaToken,
  Confirmation,
  Sca,
  Declined,
  ScaEnrollment,
  BeneficiariesVerification,
}

type Props = WireTransferApproverModalContainerProps & {
  showNotEnoughFundsWarning: boolean;
  alternateOperationId?: string;
};
const missingSCAErrors = [
  'accountOwnerIsMissingMfaPhoneFactor',
  'unableToCreateScaProcedure',
];

const WireTransferApproverModal = ({
  isApproving,
  showNotEnoughFundsWarning,
  isOpen,
  isLoading,
  approvalAuthType,
  billType,
  selectedBatchId,
  selectedPayments,
  onClose,
  onApprove,
  onComplete,
  alternateOperationId,
}: Props) => {
  const { t } = useTranslation('global');
  const { dangerNotif } = useNotifications();
  const getVerifyBeneficiariesErrorMessage =
    VerifyBeneficiariesQueryFactory.useVerifyTransfersBeneficiariesErrorMessage();
  const [currentStep, setCurrentStep] = useState<ApproverModalStep>(
    ApproverModalStep.Summary,
  );
  const [unverifiedBeneficiaries, setUnverifiedBeneficiaries] = useState<
    Beneficiary.VerificationResult[]
  >([]);
  const [isAlternateModalOpened, setIsAlternateModalOpened] = useState(false);

  const verifyTransfersBeneficiaries = useVerifyTransfersBeneficiaries();
  const factors = useGetUserFactors();

  useEffect(() => {
    if (showNotEnoughFundsWarning) {
      setCurrentStep(ApproverModalStep.NotEnoughFunds);
    }
    if (
      !showNotEnoughFundsWarning &&
      currentStep === ApproverModalStep.NotEnoughFunds
    ) {
      setCurrentStep(ApproverModalStep.Summary);
    }
  }, [showNotEnoughFundsWarning, isOpen]);

  const handleContinue = () => {
    setCurrentStep(ApproverModalStep.Summary);
  };

  useEffect(() => {
    if (approvalAuthType) {
      setCurrentStep(ApproverModalStep.Sca);
    }
  }, [approvalAuthType]);

  const handleApprove = async () => {
    try {
      await onApprove();
    } catch (error) {
      if (
        missingSCAErrors.includes(error.response?.data?.reason) ||
        missingSCAErrors.includes(error.data?.reason)
      ) {
        setCurrentStep(ApproverModalStep.ScaEnrollment);
      } else {
        dangerNotif(t('wiretransfer.confirmation.confirmApi.error'));
      }
    }
  };
  const handleAlternateAuthMethod = async (
    selectedFactor: AuthenticationFactor,
  ) => {
    if (selectedFactor.type === 'SMS') {
      await onApprove('mfa');
    } else if (selectedFactor.type === 'TOTP') {
      await onApprove('totp');
    }
  };

  const handleMfaTokenValidated = () => {
    setCurrentStep(ApproverModalStep.Confirmation);
    setIsAlternateModalOpened(false);
  };

  const handleScaProceed = (state: ScaState): void => {
    if (state === 'approved') {
      setCurrentStep(ApproverModalStep.Confirmation);
    } else if (['rejected', 'timeout'].includes(state)) {
      setCurrentStep(ApproverModalStep.Declined);
    }
  };

  const handleComplete = () => {
    handleClose();
    onComplete();
  };

  const handleClose = () => {
    setCurrentStep(ApproverModalStep.Summary);
    onClose();
  };

  const renderScaModal = () => {
    const isScaModalOpen = isOpen && currentStep === ApproverModalStep.Sca;
    return (
      <ScaWireTransferConfirmationModalContainer
        isOpen={isScaModalOpen}
        onUseAlternateMethod={() => setIsAlternateModalOpened(true)}
        onClose={handleClose}
        onScaProceed={handleScaProceed}
        factors={factors}
        setCurrentStep={setCurrentStep}
      />
    );
  };

  const renderWireTransfersDeclinedModal = () => {
    const isWireTransfersDeclinedModalOpen =
      isOpen && currentStep === ApproverModalStep.Declined;

    return (
      <WireTransfersDeclinedModal
        isOpen={isWireTransfersDeclinedModalOpen}
        onClose={handleClose}
      />
    );
  };

  const onSummaryConfirm = async () => {
    if (!billType || (!selectedPayments && !selectedBatchId)) {
      return;
    }

    const params = selectedBatchId
      ? {
          batchId: selectedBatchId,
          type: 'expense' as const,
        }
      : {
          paymentIds: selectedPayments.map((it) => it.id),
          type: billType,
        };
    try {
      const beneficiariesValidationResult =
        await verifyTransfersBeneficiaries(params);

      if (R.isFailure(beneficiariesValidationResult)) {
        setUnverifiedBeneficiaries(
          beneficiariesValidationResult.error.failedVerifications,
        );
        setCurrentStep(ApproverModalStep.BeneficiariesVerification);
      } else {
        handleApprove();
      }
    } catch (error) {
      dangerNotif(getVerifyBeneficiariesErrorMessage(error));
    }
  };

  return (
    <>
      <WireTransfersNotEnoughFundsModal
        count={selectedPayments.length}
        onContinue={handleContinue}
        isOpen={isOpen && currentStep === ApproverModalStep.NotEnoughFunds}
      />
      <AlternateAuthenticationMethodModal
        isOpen={isAlternateModalOpened}
        onClose={() => setIsAlternateModalOpened(false)}
        factors={factors}
        flow="standard"
        onAlternateAuthenticationMethodSelected={handleAlternateAuthMethod}
        onScaVerified={handleMfaTokenValidated}
        operationId={alternateOperationId}
      />
      <WireTransfersSummaryModal
        selectedPayments={selectedPayments}
        isApproving={isApproving}
        isLoading={isLoading}
        canApprove
        showNotEnoughFundsWarning={showNotEnoughFundsWarning}
        onApprove={onSummaryConfirm}
        onClose={handleClose}
        isOpen={isOpen && currentStep === ApproverModalStep.Summary}
      />
      {renderScaModal()}
      {renderWireTransfersDeclinedModal()}
      <WireTransfersApprovedModal
        count={selectedPayments.length}
        onComplete={handleComplete}
        isOpen={isOpen && currentStep === ApproverModalStep.Confirmation}
      />
      <ScaWireTransferConfirmationEnrollmentModal
        isOpen={isOpen && currentStep === ApproverModalStep.ScaEnrollment}
        onClose={() => {
          handleClose();
        }}
        onEnroll={onApprove}
      />

      <VerificationOfBeneficiariesModal
        isOpen={
          isOpen && currentStep === ApproverModalStep.BeneficiariesVerification
        }
        unverifiedBeneficiaries={unverifiedBeneficiaries}
        billType={billType}
        onCancel={handleClose}
        onConfirm={handleApprove}
      />
    </>
  );
};

export default WireTransferApproverModal;
